import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorBlack,
} from 'src/app/shared/all.constants';
import {
    OTHERS_TRANSFORMER_EVOL_CHART,
    TRANSFORMER_GENERATION_CHART,
    TRANSFORMER_HIGH_CABINS_CHART,
    TRANSFORMER_MEAN_AGE_CHART,
    TRANSFORMER_SUBSTATIONS_KPIS,
    URBAN_RURAL_SUBSTATIONS_CHART,
} from '../../assets/keys';
import { convertNumber } from 'src/app/shared/all.helpers';

const KEYS = [
    TRANSFORMER_SUBSTATIONS_KPIS,
    OTHERS_TRANSFORMER_EVOL_CHART,
    TRANSFORMER_GENERATION_CHART,
    TRANSFORMER_HIGH_CABINS_CHART,
    TRANSFORMER_MEAN_AGE_CHART,
    URBAN_RURAL_SUBSTATIONS_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    numberLvMvStations?: number;
    numberLvMvStationsEvolution?: number;

    numberLvMvTransformers?: number;
    numberLvMvTransformersEvolution?: number;

    averageAgeStations?: number;
    averageAgeStationsEvolution?: number;

    averageAgeTransformers?: number;
    averageAgeTransformersEvolution?: number;
}

interface SubstationsChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=144
    totalNumberOfStations?: number;
    //kpi_id=150
    poleMountedStations?: number;
    //kpi_id=151
    masonryStations?: number;
    //kpi_id=152
    prefabricatedStations?: number;
    error?: boolean;
}

interface TransformersGenerationChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    transformers400?: number;
    transformers410?: number;
    transformersTotal?: number;
    error?: boolean;
}

interface HighCabinsChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    urbanHighCabins?: number;
    ruralHighCabins?: number;
    totalHighCabins?: number;
    error?: boolean;
}

interface MeanAgeChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    transformersMeanAge?: number;
    substationsMeanAge?: number;
    poleMountedSubstationsMeanAge?: number;
    masonrySubstationsMeanAge?: number;
    prefabSubstationsMeanAge?: number;
    error?: boolean;
}

interface UrbanRuralSubstationsChartData {
    organizationId?: string;
    year?: string;
    ruralSubstations?: number;
    urbanSubstations?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-transformer-substations',
    templateUrl: './transformer-substations.component.html',
    styleUrls: ['./transformer-substations.component.scss'],
})
export class TransformerSubstationsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.transformerStations.tabs.substationsTransformers.texts;
    public substationsChartTitle = this.texts.lvMvEvolutionStationsChart.title;
    public transformersGenerationChartTitle = this.texts.transformersGenerationChart.title;
    public highCabinsChartTitle = this.texts.highCabinsChart.title;
    public transformersMeanAgeChartTitle = this.texts.transformersMeanAgeChart.title;
    public urbanRuralSubstationsChartTitle = this.texts.urbanRuralSubstationsChart.title;
    public substationsChartSeries: ApexAxisChartSeries = [];
    public transformersGenerationChartSeries: ApexAxisChartSeries = [];
    public highCabinsChartSeries: ApexAxisChartSeries = [];
    public transformersMeanAgeChartSeries: ApexAxisChartSeries = [];
    public urbanRuralSubstationsChartSeries: number[] = [];
    public substationsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.lvMvEvolutionStationsChart.totalTitle),
    };
    public transformersGenerationDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.transformersGenerationChart.totalTitle),
    };
    public highCabinsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.highCabinsChart.totalTitle),
    };
    public transformersMeanAgeDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: 1,
            1: 1,
            2: 1,
            3: 1,
            4: 1,
        }),
    };
    public urbanRuralSubstationsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: (val: number) =>
            convertNumber(val, this.texts.urbanRuralSubstationsChart.precision) + '%',
    };
    public urbanRuralSubstationsLabels: string[] = [];
    public urbanRuralSubstationsColors: string[] = [colorAec3Green, colorAec1BlueLight];

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(TRANSFORMER_SUBSTATIONS_KPIS),
    ) as Signal<KpiData[]>;

    public substationsChartData: Signal<SubstationsChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(OTHERS_TRANSFORMER_EVOL_CHART),
    ) as Signal<SubstationsChartData[]>;

    public transformersGenerationChartData: Signal<TransformersGenerationChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(TRANSFORMER_GENERATION_CHART),
        ) as Signal<TransformersGenerationChartData[]>;

    public highCabinsChartData: Signal<HighCabinsChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(TRANSFORMER_HIGH_CABINS_CHART),
    ) as Signal<HighCabinsChartData[]>;

    public urbanRuralSubstationsChartData: Signal<UrbanRuralSubstationsChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(URBAN_RURAL_SUBSTATIONS_CHART),
        ) as Signal<UrbanRuralSubstationsChartData[]>;

    public transformersMeanAgeChartData: Signal<MeanAgeChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(TRANSFORMER_MEAN_AGE_CHART),
    ) as Signal<MeanAgeChartData[]>;

    public substationsYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.lvMvEvolutionStationsChart.yaxisTitleLeft,
            },
        },
    ];

    public transformersGenerationYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.transformersGenerationChart.yaxisTitleLeft,
            },
        },
    ];

    public highCabinsYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.highCabinsChart.yaxisTitleLeft,
            },
        },
    ];

    public transformersMeanAgeYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
            title: {
                text: this.texts.transformersMeanAgeChart.yaxisTitleLeft,
            },
        },
    ];

    public substationsChartError: boolean = true;
    public transformersGenerationChartError: boolean = true;
    public highCabinsChartError: boolean = true;
    public transformersMeanAgeChartError: boolean = true;
    public urbanRuralSubstationsChartError: boolean = true;

    public transformersMeanAgeGrid: ApexGrid = { padding: { left: 30 } };

    public substationsLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec3Green, colorAec6Gray, colorAec4Orange, colorBlack],
        },
    };
    public transformersGenerationLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec1BlueLight, colorAec4Orange, colorBlack],
        },
    };
    public highCabinsLegend: ApexLegend = {
        markers: {
            fillColors: [colorAec3Green, colorAec1BlueLight, colorBlack],
        },
    };

    public substationsCustomTooltip: ApexTooltip['custom'];
    public transformersGenerationCustomTooltip: ApexTooltip['custom'];
    public highCabinsCustomTooltip: ApexTooltip['custom'];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const substationsDataList = this.substationsChartData();
            const transformersGenerationDataList = this.transformersGenerationChartData();
            const highCabinsDataList = this.highCabinsChartData();
            const transformersMeanAgeDataList = this.transformersMeanAgeChartData();
            const urbanRuralSubstationsDataList = this.urbanRuralSubstationsChartData();
            this.substationsChartError = this.checkForError(substationsDataList, [
                'xYear',
                'poleMountedStations',
                'masonryStations',
                'prefabricatedStations',
            ]);
            this.transformersGenerationChartError = this.checkForError(
                transformersGenerationDataList,
                ['xYear', 'transformers400', 'transformers410'],
            );
            this.highCabinsChartError = this.checkForError(highCabinsDataList, [
                'xYear',
                'ruralHighCabins',
                'urbanHighCabins',
            ]);
            this.transformersMeanAgeChartError = this.checkForError(transformersMeanAgeDataList, [
                'xYear',
                'poleMountedSubstationsMeanAge',
                'masonrySubstationsMeanAge',
                'prefabSubstationsMeanAge',
                'substationsMeanAge',
                'transformersMeanAge',
            ]);
            this.urbanRuralSubstationsChartError = this.checkForError(
                urbanRuralSubstationsDataList,
                ['urbanSubstations', 'ruralSubstations'],
            );

            if (!this.substationsChartError) {
                const totales = this.getTotales(substationsDataList, [
                    'poleMountedStations',
                    'masonryStations',
                    'prefabricatedStations',
                ]);
                this.substationsChartSeries = [
                    {
                        name: this.texts.lvMvEvolutionStationsChart.poleMountedStationsAxisTitle,
                        type: 'column',
                        data: substationsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.poleMountedStations,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.lvMvEvolutionStationsChart.masonryStationsAxisTitle,
                        type: 'column',
                        data: substationsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.masonryStations,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.lvMvEvolutionStationsChart.prefabricatedStationsAxisTitle,
                        type: 'column',
                        data: substationsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.prefabricatedStations,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.lvMvEvolutionStationsChart.totalTitle,
                        type: 'line',
                        data: substationsDataList.map((data, index) => ({
                            x: data.xYear,
                            y: totales[index],
                        })),
                        color: 'transparent',
                    },
                ];
                this.substationsDataLabels.enabledOnSeries = [0, 1, 2, 3];
            }

            if (!this.transformersGenerationChartError) {
                const totales = this.getTotales(transformersGenerationDataList, [
                    'transformers410',
                    'transformers400',
                ]);
                this.transformersGenerationChartSeries = [
                    {
                        name: this.texts.transformersGenerationChart.transformers410AxisTitle,
                        type: 'column',
                        data: transformersGenerationDataList.map((data) => ({
                            x: data.xYear,
                            y: data.transformers410,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.transformersGenerationChart.transformers400AxisTitle,
                        type: 'column',
                        data: transformersGenerationDataList.map((data) => ({
                            x: data.xYear,
                            y: data.transformers400,
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.transformersGenerationChart.totalTitle,
                        type: 'line',
                        data: transformersGenerationDataList.map((data, index) => ({
                            x: data.xYear,
                            y: totales[index],
                        })),
                        color: 'transparent',
                    },
                ];
                this.transformersGenerationDataLabels.enabledOnSeries = [0, 1, 2];
            }

            if (!this.highCabinsChartError) {
                const totales = this.getTotales(highCabinsDataList, [
                    'ruralHighCabins',
                    'urbanHighCabins',
                ]);
                this.highCabinsChartSeries = [
                    {
                        name: this.texts.highCabinsChart.ruralHighCabinsAxisTitle,
                        type: 'column',
                        data: highCabinsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.ruralHighCabins,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.highCabinsChart.urbanHighCabinsAxisTitle,
                        type: 'column',
                        data: highCabinsDataList.map((data) => ({
                            x: data.xYear,
                            y: data.urbanHighCabins,
                        })),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.highCabinsChart.totalTitle,
                        type: 'line',
                        data: highCabinsDataList.map((data, index) => ({
                            x: data.xYear,
                            y: totales[index],
                        })),
                        color: 'transparent',
                    },
                ];
                this.highCabinsDataLabels.enabledOnSeries = [0, 1, 2];
            }

            if (!this.transformersMeanAgeChartError) {
                this.transformersMeanAgeChartSeries = [
                    {
                        name: this.texts.transformersMeanAgeChart.transformersMeanAgeAxisTitle,
                        type: 'line',
                        data: transformersMeanAgeDataList.map((data) => ({
                            x: data.xYear,
                            y: data.transformersMeanAge
                                ? parseFloat(data.transformersMeanAge.toFixed(1))
                                : null,
                        })),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.transformersMeanAgeChart.substationsMeanAgeAxisTitle,
                        type: 'line',
                        data: transformersMeanAgeDataList.map((data) => ({
                            x: data.xYear,
                            y: data.substationsMeanAge,
                        })),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.transformersMeanAgeChart
                            .poleMountedSubstationsMeanAgeAxisTitle,
                        type: 'line',
                        data: transformersMeanAgeDataList.map((data) => ({
                            x: data.xYear,
                            y: data.poleMountedSubstationsMeanAge,
                        })),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.transformersMeanAgeChart
                            .masonrySubstationsMeanAgeAxisTitle,
                        type: 'line',
                        data: transformersMeanAgeDataList.map((data) => ({
                            x: data.xYear,
                            y: data.masonrySubstationsMeanAge,
                        })),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.transformersMeanAgeChart.prefabSubstationsMeanAgeAxisTitle,
                        type: 'line',
                        data: transformersMeanAgeDataList.map((data) => ({
                            x: data.xYear,
                            y: data.prefabSubstationsMeanAge,
                        })),
                        color: colorAec1BlueLight,
                    },
                ];
                this.transformersMeanAgeDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            if (!this.urbanRuralSubstationsChartError) {
                this.urbanRuralSubstationsLabels = [
                    this.texts.urbanRuralSubstationsChart.ruralSubstationsTitle,
                    this.texts.urbanRuralSubstationsChart.urbanSubstationsTitle,
                ];
                this.urbanRuralSubstationsChartSeries = [
                    urbanRuralSubstationsDataList[0].ruralSubstations || 0,
                    urbanRuralSubstationsDataList[0].urbanSubstations || 0,
                ];
                this.urbanRuralSubstationsDataLabels.enabledOnSeries = [0, 1];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.substationsCustomTooltip = this.setSubstationsCustomTooltip();
        this.transformersGenerationCustomTooltip = this.setTransformersGenerationCustomTooltip();
        this.highCabinsCustomTooltip = this.setHighCabinsCustomTooltip();
    }

    private setSubstationsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const labels = w.globals.labels;
            const title = labels[dataPointIndex];

            const precision = this.texts.lvMvEvolutionStationsChart.precision;
            const poleMountedStationsSerie = w.globals.initialSeries[0];
            const {
                name: poleMountedStationsName,
                color: poleMountedStationsColor,
                valueStringified: poleMountedStationsStringified,
            } = this.setCustomTooltipData(precision, poleMountedStationsSerie, dataPointIndex);

            const masonryStationsSerie = w.globals.initialSeries[1];
            const {
                name: masonryStationsName,
                color: masonryStationsColor,
                valueStringified: masonryStationsStringified,
            } = this.setCustomTooltipData(precision, masonryStationsSerie, dataPointIndex);

            const prefabricatedStationsSerie = w.globals.initialSeries[2];
            const {
                name: prefabricatedStationsName,
                color: prefabricatedStationsColor,
                valueStringified: prefabricatedStationsStringified,
            } = this.setCustomTooltipData(precision, prefabricatedStationsSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[3];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: poleMountedStationsColor,
                    name: poleMountedStationsName,
                    value: poleMountedStationsStringified,
                },
                {
                    color: masonryStationsColor,
                    name: masonryStationsName,
                    value: masonryStationsStringified,
                },
                {
                    color: prefabricatedStationsColor,
                    name: prefabricatedStationsName,
                    value: prefabricatedStationsStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setTransformersGenerationCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const labels = w.globals.labels;
            const title = labels[dataPointIndex];

            const precision = this.texts.transformersGenerationChart.precision;
            const transformers410Serie = w.globals.initialSeries[0];
            const {
                name: transformers410Name,
                color: transformers410Color,
                valueStringified: transformers410Stringified,
            } = this.setCustomTooltipData(precision, transformers410Serie, dataPointIndex);

            const transformers400Serie = w.globals.initialSeries[1];
            const {
                name: transformers400Name,
                color: transformers400Color,
                valueStringified: transformers400Stringified,
            } = this.setCustomTooltipData(precision, transformers400Serie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[2];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: transformers410Color,
                    name: transformers410Name,
                    value: transformers410Stringified,
                },
                {
                    color: transformers400Color,
                    name: transformers400Name,
                    value: transformers400Stringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setHighCabinsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const labels = w.globals.labels;
            const title = labels[dataPointIndex];

            const precision = this.texts.highCabinsChart.precision;
            const ruralHighCabinsSerie = w.globals.initialSeries[0];
            const {
                name: ruralHighCabinsName,
                color: ruralHighCabinsColor,
                valueStringified: ruralHighCabinsStringified,
            } = this.setCustomTooltipData(precision, ruralHighCabinsSerie, dataPointIndex);

            const urbanHighCabinsSerie = w.globals.initialSeries[1];
            const {
                name: urbanHighCabinsName,
                color: urbanHighCabinsColor,
                valueStringified: urbanHighCabinsStringified,
            } = this.setCustomTooltipData(precision, urbanHighCabinsSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[2];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: ruralHighCabinsColor,
                    name: ruralHighCabinsName,
                    value: ruralHighCabinsStringified,
                },
                {
                    color: urbanHighCabinsColor,
                    name: urbanHighCabinsName,
                    value: urbanHighCabinsStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
}
